import { GridApi, CsvCell } from 'ag-grid-community';
import type { CsvExportParams } from 'ag-grid-community/dist/types/src/interfaces/exportParams';
import type { ExcelExportParams } from 'ag-grid-community/dist/types/src/interfaces/iExcelCreator';

/**
 * Patches the Excel export method to customize the sheet name
 * @param gridApi The AG Grid API
 * @param getSheetName A function that returns the sheet name to use
 */
export function patchExcelExport(gridApi: GridApi, getSheetName: () => string): void {
    // Store a reference to the original exportDataAsExcel method
    const originalExportDataAsExcel = gridApi.exportDataAsExcel;
    
    // Override the exportDataAsExcel method to include our custom sheet name
    gridApi.exportDataAsExcel = (params: ExcelExportParams = {}) => {
        const sheetName = getSheetName();
        // Set the sheet name in the export parameters
        params.sheetName = sheetName || params.sheetName;
        // Call the original method with our modified parameters
        return originalExportDataAsExcel.call(gridApi, params);
    };
}

/**
 * Patches the CSV export method to add a line with a custom label
 * @param gridApi The AG Grid API
 * @param getRowValue
 */
export function patchCsvExport(gridApi: GridApi, getRowValue: () => string): void {
    // Store a reference to the original exportDataAsCsv method
    const originalExportDataAsCsv = gridApi.exportDataAsCsv;

    // Override the exportDataAsCsv method to include our custom information
    gridApi.exportDataAsCsv = (params: CsvExportParams = {}) => {
        // Get our custom sheet name (same as Excel for consistency)
        const newRowValue = getRowValue();

        // return default call if there is not custom label to add
        if (!newRowValue) {
            return originalExportDataAsCsv.call(gridApi, params);
        }

        // Get the number of columns in the grid
        const columnCount = params.columnKeys?.length || 0;

        // Helper function to create a cell with a specific value
        const createCell = (value: string): CsvCell => ({
            data: { value }
        });

        // Create custom rows with report information
        const reportNameRow: CsvCell[] = [createCell(newRowValue)];

        // Fill the rest of the rows with empty cells
        for (let i = 1; i < columnCount; i++) {
            reportNameRow.push(createCell(""));
        }

        // Set the custom content with multiple rows
        params.appendContent = [reportNameRow];
        return originalExportDataAsCsv.call(gridApi, params);
    };
}